import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, X, Send, Loader2, Headset, Check } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { createSupportEnquiry } from '@/lib/supportEnquiry';

const SUGGESTIONS = [
  'How do I book a session?',
  'What is your returns policy?',
  'Where are you located?',
  'Opening hours',
];

export default function SupportChatbot({ settings, open, onClose, hideLauncher = false }) {
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'bot',
      text: "Hi! I'm the Motion Reality assistant. Ask me about bookings, products, returns, or anything else and I'll do my best to help.",
    },
  ]);
  const [handoff, setHandoff] = useState(false);
  const [handoffBusy, setHandoffBusy] = useState(false);
  const [handoffDone, setHandoffDone] = useState(null);
  const [hf, setHf] = useState({ full_name: '', email: '', message: '' });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, handoff, handoffDone]);

  const buildSystemPrompt = () => {
    const s = settings || {};
    return `You are the friendly customer-service assistant for Motion Reality, a premium professional-grade motion racing simulation hardware store and cockpit booking platform based in Bromborough, Merseyside, UK.

Use ONLY these business facts when answering. If you don't know something specific (e.g. a live order status), tell the customer to contact the team directly — do not invent details.

BUSINESS FACTS:
- Name: Motion Reality Sim Racing
- Location: Unit 9, Croft Business Park, Carrock Road, Bromborough, Merseyside, CH62 3RA, UK
- Phone: ${s.phone_number || '+44 151 528 3001'}
- Email: ${s.email || 'sales@motionreality.co.uk'}
- Website: ${s.website_url || 'https://motionreality.co.uk'}
- Opening hours: ${s.hours || 'Mon–Fri 9am–6pm · Sat 10am–4pm'}

BOOKINGS:
- Experiences: Adult Sim Racing (ages 16+), Child Sim Racing, Young Racer sessions.
- Sessions are bookable online at /bookings by date, time slot and rig.
- Loyalty scheme: earn 1 point per £1 spent; 100 points = £1 discount; 1 free session every 20 qualifying sessions.

PRODUCTS / SHOP:
- Premium sim racing hardware (wheelbases, steering wheels, pedals, cockpits, mounts, accessories) sold via the /shop.
- Stock and pricing are managed via Square.

RETURNS / REFUNDS:
- 14-day exchange/return window from delivery.
- 10% restocking fee for non-faulty returns.
- Faulty items replaced or refunded in full.
- Full policy on /refund-policy; start a return at /returns.

SUPPORT CHANNELS:
- Live chat / chatbot (you): available via the top-bar Live Chat button.
- Contact form: /contact.
- Customer service page: /customer-service (has a support request form).
- You can offer a "Talk to a human" hand-off that logs a support ticket for the team.

Keep replies short, helpful and friendly. If a question is about a specific order or account, offer the human hand-off or direct the customer to email or call the team.`;
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput('');
    const next = [...messages, { role: 'user', text: msg }];
    setMessages(next);
    setSending(true);
    try {
      const res = await base44.integrations.Core.InvokeLLM({
        prompt: `${buildSystemPrompt()}\n\nCustomer: ${msg}\nAssistant:`,
        add_context_from_internet: false,
      });
      const reply = typeof res === 'string' ? res : res?.text || res?.response || "I'm not sure right now — please contact the team and they'll help.";
      setMessages((m) => [...m, { role: 'bot', text: reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'bot', text: "Sorry, I couldn't reach the answer service. Please call or email the team and they'll help straight away." }]);
    } finally {
      setSending(false);
    }
  };

  const submitHandoff = async (e) => {
    e.preventDefault();
    if (handoffBusy) return;
    if (!hf.full_name.trim() || !hf.email.trim() || !hf.message.trim()) return;
    setHandoffBusy(true);
    try {
      const res = await createSupportEnquiry({
        full_name: hf.full_name.trim(),
        email: hf.email.trim(),
        phone: '',
        enquiry_category: 'general_support',
        message: hf.message.trim(),
        source: 'chat_handoff',
      });
      setHandoffDone(res.reference);
      setMessages((m) => [
        ...m,
        { role: 'bot', text: `Thanks ${hf.full_name.trim().split(' ')[0]} — I've logged your request with the team as ticket #${res.reference}. They'll be in touch by email. Anything else I can help with in the meantime?` },
      ]);
      setHf({ full_name: '', email: '', message: '' });
      setHandoff(false);
    } catch (err) {
      setMessages((m) => [...m, { role: 'bot', text: "I couldn't submit your request just now — please email or call the team and they'll help straight away." }]);
    } finally {
      setHandoffBusy(false);
    }
  };

  return (
    <>
      {/* Floating launcher — hidden when the navbar launcher drives the widget */}
      {!hideLauncher && (
        <motion.button
          onClick={() => (onClose ? onClose() : null)}
          className="fixed bottom-5 left-5 z-50 flex items-center justify-center w-14 h-14 rounded-full bg-primary text-white shadow-lg shadow-primary/30 hover:bg-primary/90 transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title="Ask the Motion Reality assistant"
          aria-label="Open chat assistant"
        >
          <Bot className="w-6 h-6" />
          <span className="absolute top-0 right-0 w-3 h-3 rounded-full bg-green-400 ring-2 ring-[#0d0d0f] animate-pulse" />
        </motion.button>
      )}

      {/* Chat panel — anchored top-right under the navbar */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.97 }}
            transition={{ duration: 0.18 }}
            className="fixed z-50 w-[360px] max-w-[calc(100vw-2.5rem)] h-[480px] max-h-[calc(100vh-6rem)] bg-[#111115] border border-white/10 rounded-lg shadow-2xl flex flex-col overflow-hidden"
            style={{ top: 70, right: 20, bottom: 'auto' }}
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-[#0d0d0f]">
              <div className="relative">
                <div className="w-9 h-9 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-primary" />
                </div>
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-green-400 ring-2 ring-[#0d0d0f]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-sm leading-none">Motion Reality Assistant</p>
                <p className="text-green-400 text-[10px] font-bold tracking-widest mt-1">● ONLINE</p>
              </div>
              <button onClick={onClose} className="text-white/40 hover:text-white transition-colors" aria-label="Close chat">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[80%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap break-words ${
                      m.role === 'user'
                        ? 'bg-primary text-white rounded-br-sm'
                        : 'bg-white/5 text-white/80 border border-white/5 rounded-bl-sm'
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-white/5 border border-white/5 px-3 py-2 rounded-lg rounded-bl-sm flex items-center gap-1.5">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-white/50" />
                    <span className="text-white/40 text-xs">typing…</span>
                  </div>
                </div>
              )}
            </div>

            {/* Suggestions */}
            {messages.length <= 2 && !sending && !handoff && (
              <div className="px-4 pb-2 flex flex-wrap gap-1.5">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="text-[11px] font-medium text-white/60 hover:text-white border border-white/10 hover:border-primary/40 hover:bg-primary/5 px-2.5 py-1 rounded-full transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {/* Human hand-off inline form */}
            {handoff ? (
              <form onSubmit={submitHandoff} className="p-3 border-t border-white/10 bg-[#0d0d0f] space-y-2">
                <p className="text-primary text-[10px] font-bold tracking-widest">TALK TO A HUMAN</p>
                <input
                  value={hf.full_name}
                  onChange={(e) => setHf((v) => ({ ...v, full_name: e.target.value }))}
                  placeholder="Full name *"
                  className="w-full bg-[#111115] border border-white/10 text-white text-sm px-3 py-2 rounded-md focus:outline-none focus:border-primary/50"
                  required
                />
                <input
                  type="email"
                  value={hf.email}
                  onChange={(e) => setHf((v) => ({ ...v, email: e.target.value }))}
                  placeholder="Email *"
                  className="w-full bg-[#111115] border border-white/10 text-white text-sm px-3 py-2 rounded-md focus:outline-none focus:border-primary/50"
                  required
                />
                <textarea
                  value={hf.message}
                  onChange={(e) => setHf((v) => ({ ...v, message: e.target.value }))}
                  placeholder="How can the team help? *"
                  rows={2}
                  className="w-full bg-[#111115] border border-white/10 text-white text-sm px-3 py-2 rounded-md focus:outline-none focus:border-primary/50 resize-none"
                  required
                />
                <div className="flex gap-2">
                  <button type="button" onClick={() => setHandoff(false)} className="flex-1 border border-white/10 hover:border-white/20 text-white/60 hover:text-white text-xs font-bold tracking-wider py-2 rounded-md transition-colors">
                    CANCEL
                  </button>
                  <button type="submit" disabled={handoffBusy} className="flex-1 flex items-center justify-center gap-1.5 bg-primary hover:bg-primary/90 text-white text-xs font-bold tracking-wider py-2 rounded-md disabled:opacity-50 transition-colors">
                    {handoffBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    SUBMIT
                  </button>
                </div>
              </form>
            ) : (
              <div className="p-3 border-t border-white/10 bg-[#0d0d0f]">
                <div className="flex items-center gap-2">
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Type your question…"
                    className="flex-1 bg-[#111115] border border-white/10 text-white text-sm px-3 py-2.5 rounded-md focus:outline-none focus:border-primary/50 transition-colors"
                  />
                  <button
                    onClick={() => send()}
                    disabled={sending || !input.trim()}
                    className="flex items-center justify-center w-10 h-10 rounded-md bg-primary text-white hover:bg-primary/90 disabled:opacity-40 transition-colors shrink-0"
                    aria-label="Send"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-white/20 text-[10px]">AI assistant · account specifics → human</p>
                  <button
                    onClick={() => setHandoff(true)}
                    className="inline-flex items-center gap-1 text-primary hover:text-primary/80 text-[10px] font-bold tracking-widest transition-colors"
                  >
                    <Headset className="w-3 h-3" /> TALK TO A HUMAN
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}